import { useCallback, useEffect, useRef, useState, type DependencyList } from 'react';
import { errorMessage } from './api';

/** Load data for a view, with loading/error state and a manual reload. */
export function useLoad<T>(loader: () => Promise<T>, deps: DependencyList) {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const generation = useRef(0);

  const reload = useCallback(
    async (silent = false) => {
      const current = ++generation.current;
      if (!silent) setLoading(true);
      try {
        const result = await loader();
        // A newer request may have started while this one was in flight.
        if (current === generation.current) {
          setData(result);
          setError(null);
        }
      } catch (err) {
        if (current === generation.current && !silent) setError(errorMessage(err));
      } finally {
        if (current === generation.current) setLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- callers pass the loader's own dependencies
    deps,
  );

  useEffect(() => {
    void reload();
  }, [reload]);

  return { data, setData, error, loading, reload };
}

export interface ProjectEvent {
  type: string;
  commentId?: string;
  itemId?: string;
}

type EventHandler = (event: ProjectEvent) => void;

interface Channel {
  source: EventSource | null;
  handlers: Set<EventHandler>;
  /** Events may have been missed: the stream dropped, or was released while the tab was hidden. */
  stale: boolean;
}

/*
 * One stream per project per tab, however many components listen. Browsers allow
 * six HTTP/1.1 connections per site across all tabs, and every open stream holds
 * one, so a hidden tab gives its stream back after a minute.
 */
const channels = new Map<string, Channel>();
const HIDDEN_RELEASE_MS = 60_000;
let releaseTimer: ReturnType<typeof setTimeout> | null = null;
let watchingVisibility = false;

function emit(channel: Channel, event: ProjectEvent) {
  for (const handler of [...channel.handlers]) {
    try {
      handler(event);
    } catch {
      /* one listener failing must not stop the others */
    }
  }
}

function connect(projectId: string, channel: Channel) {
  if (channel.source || document.hidden) return;
  const source = new EventSource(`/api/projects/${projectId}/events`);
  source.onmessage = (message) => {
    try {
      emit(channel, JSON.parse(message.data));
    } catch {
      /* ignore malformed events */
    }
  };
  source.onerror = () => {
    channel.stale = true; // EventSource reconnects by itself; what happened meanwhile is unknown
  };
  source.onopen = () => {
    if (!channel.stale) return;
    channel.stale = false;
    emit(channel, { type: 'resync' });
  };
  channel.source = source;
}

function disconnect(channel: Channel) {
  channel.source?.close();
  channel.source = null;
}

function watchVisibility() {
  if (watchingVisibility) return;
  watchingVisibility = true;
  document.addEventListener('visibilitychange', () => {
    if (releaseTimer) clearTimeout(releaseTimer);
    releaseTimer = null;
    if (document.hidden) {
      releaseTimer = setTimeout(() => {
        for (const channel of channels.values()) {
          if (!channel.source) continue;
          disconnect(channel);
          channel.stale = true;
        }
      }, HIDDEN_RELEASE_MS);
    } else {
      for (const [projectId, channel] of channels) connect(projectId, channel);
    }
  });
}

/**
 * Subscribe to a project's live events. After a dropped connection the handler
 * gets one `{ type: 'resync' }` event: reload whatever is on screen.
 */
export function useProjectEvents(projectId: string | undefined, onEvent: EventHandler) {
  const handler = useRef(onEvent);
  handler.current = onEvent;

  useEffect(() => {
    if (!projectId) return;
    watchVisibility();
    let channel = channels.get(projectId);
    if (!channel) {
      channel = { source: null, handlers: new Set(), stale: false };
      channels.set(projectId, channel);
    }
    const listener: EventHandler = (event) => handler.current(event);
    channel.handlers.add(listener);
    connect(projectId, channel);
    const current = channel;
    return () => {
      current.handlers.delete(listener);
      if (current.handlers.size === 0) {
        disconnect(current);
        channels.delete(projectId);
      }
    };
  }, [projectId]);
}

/** Size of an element, updated as it resizes. */
export function useElementSize<T extends HTMLElement>() {
  const ref = useRef<T>(null);
  const [size, setSize] = useState({ width: 0, height: 0 });

  useEffect(() => {
    const element = ref.current;
    if (!element) return;
    const observer = new ResizeObserver(([entry]) => {
      setSize({ width: entry.contentRect.width, height: entry.contentRect.height });
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  return [ref, size] as const;
}

export function useDocumentTitle(title: string | null | undefined) {
  useEffect(() => {
    document.title = title ? `${title} · Revisions` : 'Revisions by MK-Way';
  }, [title]);
}
